package com.partejar.app.feature.contratos.presentation

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp

@Composable
internal fun ContractSignaturePad(
    strokes: List<List<SignaturePoint>>,
    onChange: (List<List<SignaturePoint>>) -> Unit,
    modifier: Modifier = Modifier
) {
    val ink = MaterialTheme.colorScheme.onSurface
    val outline = MaterialTheme.colorScheme.outline
    val currentStrokes by rememberUpdatedState(strokes)
    var canvasSize by remember { mutableStateOf(IntSize.Zero) }

    Canvas(
        modifier
            .fillMaxWidth()
            .height(180.dp)
            .onSizeChanged { canvasSize = it }
            .pointerInput(Unit) {
                awaitEachGesture {
                    val down = awaitFirstDown(requireUnconsumed = false)
                    val baseStrokes = currentStrokes
                    var activeStroke = listOf(normalizedSignaturePoint(down.position, canvasSize))
                    onChange(baseStrokes + listOf(activeStroke))
                    down.consume()
                    var drawing = true
                    while (drawing) {
                        val event = awaitPointerEvent()
                        val change = event.changes.firstOrNull { it.id == down.id }
                        if (change == null || !change.pressed) {
                            drawing = false
                        } else {
                            activeStroke = appendSignaturePoint(
                                activeStroke,
                                normalizedSignaturePoint(change.position, canvasSize)
                            )
                            onChange(baseStrokes + listOf(activeStroke))
                            change.consume()
                        }
                    }
                }
            }
    ) {
        strokes.forEach { points ->
            when (points.size) {
                0 -> Unit
                1 -> drawCircle(
                    color = ink,
                    radius = 1.8f,
                    center = Offset(points.single().x * size.width, points.single().y * size.height)
                )
                else -> {
                    val offsets = points.map { Offset(it.x * size.width, it.y * size.height) }
                    val path = Path().apply {
                        moveTo(offsets.first().x, offsets.first().y)
                        for (index in 1 until offsets.size) {
                            val previous = offsets[index - 1]
                            val current = offsets[index]
                            val midpoint = Offset(
                                (previous.x + current.x) / 2f,
                                (previous.y + current.y) / 2f
                            )
                            quadraticTo(previous.x, previous.y, midpoint.x, midpoint.y)
                        }
                        lineTo(offsets.last().x, offsets.last().y)
                    }
                    drawPath(
                        path,
                        ink,
                        style = Stroke(width = 3f, cap = StrokeCap.Round, join = StrokeJoin.Round)
                    )
                }
            }
        }
        drawLine(outline, Offset(0f, size.height - 1f), Offset(size.width, size.height - 1f), 1f)
    }
}
